/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-call control bar — Design-System variant, pixel-matched to Figma
// (Ping Design system node 889:11587 "In call bottom nav / desktop" → Variant
// =Default, 1920×60, fill #111318, pad[12,24]). The bar lays its three groups
// out SPACE_BETWEEN across the full width:
//   • Mic-video pill (106×36 #222429 r12 p4 gap8) — mic + camera each a joined
//     split-button: a 28px toggle (radius 8/0/0/8) + a 16px device caret
//     (radius 0/8/8/0), 1px apart.
//   • Nav-actions pill (208×36 #222429 r12) — [share, emoji, chat, notes, add]
//     in a p4/gap8 container, then a 1px divider, then the More button.
//   • Destructive "Leave" button (#FF5353 r8, text-only per the Default variant).
// Figma icon strokes are 1.5px; dark glyphs are #E8EAED, carets #8C929E.
// Hand-raise is NOT a bar button — it lives in the Reactions menu (Actions tab).
// Tailwind-first: surfaces use design tokens (dark-surface-elevated = #222429,
// dark-surface-pressed = #2F333B) and every group has a light-mode variant.
// Pure presentation: the host owns the LiveKit media stack and passes the
// flags + handlers down.

import type React from "react";
import { forwardRef } from "react";
import { ChevronUp } from "lucide-react-native";
import { Pressable, Text, View } from "react-native";

import { useColorScheme } from "../../foundations/colorScheme";
import { CallControlIcon } from "./CallControlIcon";

export interface CallControlBarProps {
	readonly micEnabled: boolean;
	readonly cameraEnabled: boolean;
	readonly screenSharing: boolean;
	/** Highlight states for the toggle-panel buttons. */
	readonly chatActive?: boolean;
	readonly notesActive?: boolean;
	/** Unread Call-Thread count → red badge on the chat button. */
	readonly chatUnread?: number;
	readonly reactionsActive?: boolean;
	readonly onToggleMic: () => void;
	readonly onToggleCamera: () => void;
	/** Open the mic / camera device picker (caret on the split-button). */
	readonly onMicMenu?: (() => void) | undefined;
	readonly onCamMenu?: (() => void) | undefined;
	readonly onToggleScreenShare?: (() => void) | undefined;
	readonly onReactions?: (() => void) | undefined;
	readonly onOpenChat?: (() => void) | undefined;
	readonly onOpenNotes?: (() => void) | undefined;
	readonly onAddParticipant?: (() => void) | undefined;
	readonly onMore?: (() => void) | undefined;
	readonly onLeave: () => void;
	/** Refs to the trigger buttons, so the host can anchor popovers to them. */
	readonly reactionsRef?: React.Ref<View>;
	readonly moreRef?: React.Ref<View>;
	readonly micMenuRef?: React.Ref<View>;
	readonly camMenuRef?: React.Ref<View>;
}

const ICON = 20; // Figma call-control glyph box (20×20)
const CARET = "#8C929E"; // device-caret arrow tint (Figma)
const ICON_OFF = "#FF5353"; // mic-muted tint (Figma)
const ACTIVE = "#537EFF"; // accent-brand
const SHARE_ON = "#41921C"; // sharing-active green (Figma)

const GroupButton = forwardRef<
	View,
	{
		label: string;
		active?: boolean;
		badge?: number;
		/** Override the default rounding (joined split-button corners). */
		className?: string;
		onPress?: (() => void) | undefined;
		children: React.ReactNode;
	}
>(({ label, badge, className, onPress, children }, ref) => (
	<Pressable
		ref={ref}
		accessibilityRole="button"
		accessibilityLabel={
			badge !== undefined && badge > 0 ? `${label}, ${badge} unread` : label
		}
		disabled={onPress === undefined}
		onPress={onPress}
		className={`relative h-[28px] w-[28px] items-center justify-center rounded-[8px] active:opacity-70 ${className ?? ""}`}
	>
		{children}
		{badge !== undefined && badge > 0 ? (
			<View className="absolute -right-1 -top-1 h-[14px] min-w-[14px] items-center justify-center rounded-full border border-light-surface-primary bg-[#FF5353] px-[3px] dark:border-dark-surface-app">
				<Text className="text-[9px] font-bold leading-[12px] text-white">
					{badge > 9 ? "9+" : String(badge)}
				</Text>
			</View>
		) : null}
	</Pressable>
));
GroupButton.displayName = "GroupButton";

export const CallControlBar: React.FC<CallControlBarProps> = ({
	micEnabled,
	cameraEnabled,
	screenSharing,
	chatActive = false,
	notesActive = false,
	reactionsActive = false,
	chatUnread = 0,
	onToggleMic,
	onToggleCamera,
	onMicMenu,
	onCamMenu,
	onToggleScreenShare,
	onReactions,
	onOpenChat,
	onOpenNotes,
	onAddParticipant,
	onMore,
	onLeave,
	reactionsRef,
	moreRef,
	micMenuRef,
	camMenuRef,
}) => {
	// Light/dark chrome: control-bar glyphs follow the resolved theme. Dark glyph
	// = Figma #E8EAED (= dark-icon-onAccent token).
	const iconOn = useColorScheme() === "dark" ? "#E8EAED" : "#2E3138";
	const pill = "rounded-[12px] bg-[#E8EAED] dark:bg-dark-surface-elevated";
	return (
		<View className="w-full flex-row items-center justify-between gap-[8px] px-[24px]">
			{/* Mic + camera pill — two joined split-buttons (toggle + device caret). */}
			<View className={`flex-row items-center gap-[8px] p-[4px] ${pill}`}>
				{/* Mic split */}
				<View className="flex-row items-center">
					<GroupButton
						label={micEnabled ? "Mute" : "Unmute"}
						active={micEnabled}
						onPress={onToggleMic}
						className="rounded-l-[8px] rounded-r-none"
					>
						<CallControlIcon
							name={micEnabled ? "micOn" : "micOff"}
							size={ICON}
							color={micEnabled ? iconOn : ICON_OFF}
							accessibilityLabel={micEnabled ? "Mute" : "Unmute"}
						/>
					</GroupButton>
					{onMicMenu ? (
						<Pressable
							ref={micMenuRef}
							accessibilityLabel="Microphone devices"
							onPress={onMicMenu}
							className="ml-px h-[28px] w-[16px] items-center justify-center rounded-l-none rounded-r-[8px] active:opacity-70"
						>
							<ChevronUp size={12} color={CARET} />
						</Pressable>
					) : null}
				</View>
				{/* Camera split */}
				<View className="flex-row items-center">
					<GroupButton
						label={cameraEnabled ? "Turn camera off" : "Turn camera on"}
						active={cameraEnabled}
						onPress={onToggleCamera}
						className="rounded-l-[8px] rounded-r-none"
					>
						<CallControlIcon
							name={cameraEnabled ? "videoOn" : "videoOff"}
							size={ICON}
							color={iconOn}
							accessibilityLabel={cameraEnabled ? "Turn camera off" : "Turn camera on"}
						/>
					</GroupButton>
					{onCamMenu ? (
						<Pressable
							ref={camMenuRef}
							accessibilityLabel="Camera devices"
							onPress={onCamMenu}
							className="ml-px h-[28px] w-[16px] items-center justify-center rounded-l-none rounded-r-[8px] active:opacity-70"
						>
							<ChevronUp size={12} color={CARET} />
						</Pressable>
					) : null}
				</View>
			</View>

			{/* Nav-actions pill — [share, emoji, chat, notes, add] · divider · more. */}
			<View className={`flex-row items-center ${pill}`}>
				<View className="flex-row items-center gap-[8px] p-[4px]">
					<GroupButton
						label={screenSharing ? "Stop sharing" : "Share screen"}
						active={!screenSharing}
						onPress={onToggleScreenShare}
					>
						<CallControlIcon
							name={screenSharing ? "shareActive" : "share"}
							size={ICON}
							color={screenSharing ? SHARE_ON : iconOn}
						/>
					</GroupButton>
					<GroupButton ref={reactionsRef} label="Reactions" onPress={onReactions}>
						<CallControlIcon name="emoji" size={ICON} color={reactionsActive ? ACTIVE : iconOn} />
					</GroupButton>
					<GroupButton label="Call thread" onPress={onOpenChat} badge={chatUnread}>
						<CallControlIcon name="thread" size={ICON} color={chatActive ? ACTIVE : iconOn} />
					</GroupButton>
					<GroupButton label="Call notes" onPress={onOpenNotes}>
						<CallControlIcon name="notes" size={ICON} color={notesActive ? ACTIVE : iconOn} />
					</GroupButton>
					<GroupButton label="Add participant" onPress={onAddParticipant}>
						<CallControlIcon name="add" size={ICON} color={iconOn} />
					</GroupButton>
				</View>
				{/* Divider + More (Figma: a 28px sub-container with a left border). */}
				<View className="h-[36px] w-[36px] items-center justify-center border-l border-light-border-secondary dark:border-dark-surface-pressed">
					<GroupButton ref={moreRef} label="More" onPress={onMore}>
						<CallControlIcon name="more" size={ICON} color={iconOn} />
					</GroupButton>
				</View>
			</View>

			{/* Leave — text-only Destructive button (Figma Default variant). */}
			<Pressable
				accessibilityRole="button"
				accessibilityLabel="Leave call"
				onPress={onLeave}
				className="h-[36px] flex-row items-center justify-center rounded-[8px] bg-[#FF5353] px-[12px] active:opacity-80"
			>
				<Text className="text-[14px] font-bold text-[#F5F6F8]">Leave</Text>
			</Pressable>
		</View>
	);
};
